import {Fragment, useState, useEffect} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {
  HomeIcon,
  ShieldCheckIcon,
  XMarkIcon,
  CalendarDaysIcon,
  EnvelopeIcon,
  LinkIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {isBrowser, Image, Link, useUrl} from '@shopify/hydrogen';

import type {EnhancedMenu, EnhancedMenuItem} from '~/lib/utils';

interface MinimalLink {
  to: string;
  title: string;
}

export function MenuDrawer({
  title,
  menu,
  secondaryMenu = undefined,
}: {
  title: string;
  menu?: EnhancedMenu;
  secondaryMenu?: EnhancedMenu;
}) {
  const {pathname} = useUrl();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Listen for menu toggle
  if (isBrowser()) {
    window.addEventListener(
      'menu.toggle',
      () => {
        setMobileMenuOpen(!mobileMenuOpen);
      },
      false,
    );
  }

  return (
    <Transition.Root show={mobileMenuOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-40 lg:hidden"
        onClose={setMobileMenuOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-white">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex flex-shrink-0 items-center border-b py-5 pl-5">
                <Image
                  alt={title}
                  className="h-14 w-auto"
                  src="/images/believe-photography-logo.png"
                  height={240}
                  loading={'lazy'}
                  sizes="(min-width: 768px) 50vw, 100vw"
                  width={600}
                  loaderOptions={{
                    scale: 2,
                    crop: 'center',
                  }}
                />
              </div>
              <nav
                className="mt-12 h-full flex-shrink-0 divide-y divide-gray-300/30 overflow-y-auto"
                aria-label="Sidebar"
              >
                <div className="space-y-1 px-4">
                  {/* Main menu */}
                  {menu &&
                    menu?.items.map((item) => (
                      <MenuLink key={item.to} item={item} />
                    ))}
                </div>
                <div className="mt-6 pt-6">
                  <div className="space-y-1 px-2">
                    <MenuLink item={{to: '/account', title: 'Account'}} />
                    {secondaryMenu?.items.map((item) => (
                      <MenuLink key={item.to} item={item} />
                    ))}
                  </div>
                </div>
              </nav>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function MenuLink({item}: {item: MinimalLink | EnhancedMenuItem}) {
  return (
    <Link
      to={item.to}
      className={clsx(
        item.current
          ? 'bg-accent text-white'
          : 'text-gray-600 hover:bg-accent hover:text-white',
        'group flex items-center rounded-md px-2 py-2 text-base font-medium',
      )}
      aria-current={item.current ? 'page' : undefined}
    >
      <MenuItemIcon item={item} />
      {item.title}
    </Link>
  );
}

function MenuItemIcon({item}: {item: MinimalLink | EnhancedMenuItem}) {
  let Icon = LinkIcon;

  const compareTitle = item.title.toLowerCase();

  if (compareTitle.includes('home')) {
    Icon = HomeIcon;
  }

  if (compareTitle.includes('event')) {
    Icon = CalendarDaysIcon;
  }

  if (compareTitle.includes('contact')) {
    Icon = EnvelopeIcon;
  }

  if (compareTitle.includes('account')) {
    Icon = UserCircleIcon;
  }

  if (compareTitle.includes('privacy')) {
    Icon = ShieldCheckIcon;
  }

  return (
    <Icon
      className="mr-4 h-6 w-6 text-accent group-hover:text-white"
      aria-hidden="true"
    />
  );
}
